import { useState, useEffect } from 'react';

interface Sponsor {
  id: number;
  business_name: string;
  tagline: string;
  website_url: string;
  image_url: string | null;
  accent_color: string;
}

const FALLBACK_SPONSORS: Sponsor[] = [
  {
    id: 0,
    business_name: 'Your Business Here',
    tagline: 'Advertise on MarineLink - reach 10,000+ NZ marine customers',
    website_url: '/pricing#advertising',
    image_url: null,
    accent_color: '#4FB3D9',
  },
];

export default function SponsorBanner() {
  const [sponsors, setSponsors] = useState<Sponsor[]>(FALLBACK_SPONSORS);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    fetch('/.netlify/functions/advertisers')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.sponsors?.length > 0) {
          setSponsors(data.sponsors);
        }
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (sponsors.length <= 1) return;
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % sponsors.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [sponsors.length]);

  const sponsor = sponsors[index];

  return (
    <div className="w-full">
      <div
        className="relative rounded-lg overflow-hidden border border-[rgba(79,179,217,0.15)] dark:border-[rgba(79,179,217,0.2)] transition-all"
        style={{
          background: `linear-gradient(135deg, ${sponsor.accent_color}0d, ${sponsor.accent_color}05)`,
        }}
      >
        {/* Sponsored label */}
        <div className="absolute top-2 right-3 z-10">
          <span className="text-[0.6rem] text-text-muted uppercase tracking-wider font-medium">Sponsored</span>
        </div>

        <a
          href={sponsor.website_url}
          target={sponsor.website_url.startsWith('http') ? '_blank' : undefined}
          rel={sponsor.website_url.startsWith('http') ? 'noopener noreferrer' : undefined}
          className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
        >
          {/* Logo / image */}
          {sponsor.image_url ? (
            <img
              src={sponsor.image_url}
              alt={sponsor.business_name}
              className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover flex-shrink-0"
              loading="lazy"
            />
          ) : (
            <div
              className="w-12 h-12 md:w-14 md:h-14 rounded-lg flex items-center justify-center flex-shrink-0 font-display font-bold text-lg"
              style={{ backgroundColor: `${sponsor.accent_color}1a`, color: sponsor.accent_color }}
            >
              {sponsor.business_name.charAt(0)}
            </div>
          )}

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm md:text-base truncate" style={{ color: sponsor.accent_color }}>
              {sponsor.business_name}
            </div>
            <div className="text-text-secondary text-xs md:text-sm truncate">
              {sponsor.tagline}
            </div>
          </div>

          {/* Arrow */}
          <div className="flex-shrink-0 text-text-muted text-sm hidden md:block">
            →
          </div>
        </a>
      </div>

      {/* Dots indicator */}
      {sponsors.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {sponsors.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-4 bg-accent' : 'w-1.5 bg-text-muted opacity-40'
              }`}
              aria-label={`Sponsor ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
